import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Check, X, MessageSquare, CheckCircle2, Truck, FileText, Download, Zap, RefreshCw, TrendingUp,
} from "lucide-react";
import { usePlatformAuth } from "../../lib/use-platform-auth";
import { formatCurrency } from "../../lib/auction-utils";
import {
  ACCEPTED_SALE_STATUSES,
  AuctionCardWrapper,
  AuctionCardImage,
  AuctionCardHeader,
  FavoriteButton,
  FlashBadge,
  LiveTimingBadge,
  EndedTimingBadge,
  FooterStatusBanner,
  vehicleDetailsString,
} from "./AuctionCardShared";

type ManageMode = "seller" | "buyer";

type Props = {
  auction: any;
  organizationSlug: string;
  mode: ManageMode;
  isFavorite?: boolean;
  onToggleFavorite?: (id: string) => void;
  onRefresh?: () => void;
};

export function ManageAuctionCard({
  auction,
  organizationSlug,
  mode,
  isFavorite = false,
  onToggleFavorite,
  onRefresh,
}: Props) {
  const navigate = useNavigate();
  const { organizations } = usePlatformAuth();
  const [submitting, setSubmitting] = useState(false);

  // Seller decision states
  const [showCounter, setShowCounter] = useState(false);
  const [counterAmount, setCounterAmount] = useState(auction.currentBid + (auction.minimumIncrement || 1000));
  const [sellerNotes, setSellerNotes] = useState("");
  const [showRelaunch, setShowRelaunch] = useState(false);
  const [relaunchType, setRelaunchType] = useState<"regular" | "flash">(auction.type === "flash" ? "flash" : "regular");
  const [relaunchDurationMinutes, setRelaunchDurationMinutes] = useState(
    auction.durationMinutes ?? (auction.type === "flash" ? 60 : 1440),
  );

  // Buyer negotiation states
  const [showBuyerCounter, setShowBuyerCounter] = useState(false);
  const [buyerCounterAmount, setBuyerCounterAmount] = useState(auction.buyerPrice + (auction.minimumIncrement || 1000));
  const [buyerNotes, setBuyerNotes] = useState("");

  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const currentOrg = organizations.find((o) => o.slug === organizationSlug);
  const isSeller = mode === "seller";
  const isEditable = isSeller && (auction.status === "draft" || auction.status === "changes_requested");

  const v = auction.vehicle;
  const details = vehicleDetailsString(v);

  // Price display: buyer sees buyerPrice/finalPrice, seller sees sellerPayout
  const displayPrice = (() => {
    if (isSeller && auction.bidCount > 0) return auction.sellerPayout;
    // For purchases, use buyerPrice or finalPrice
    if (auction.buyerPrice) return auction.buyerPrice;
    if (auction.finalPrice) return auction.finalPrice;
    return auction.currentBid || auction.startingBid || 0;
  })();

  const priceLabel = (() => {
    if (isSeller && auction.bidCount > 0) return "Utbetaling";
    if (mode === "buyer") return "Kjøpesum";
    return auction.bidCount > 0 ? "Vinnende bud" : "Startpris";
  })();

  // --- Handlers ---

  const handleAction = async (action: "accept" | "reject" | "offers" | "relaunch") => {
    if (
      action === "accept"
      && !window.confirm("Ved å godta budet inngår du en bindende avtale om salg av bilen etter Forhandlerbørsens vilkår. Vil du fortsette?")
    ) return;

    setSubmitting(true);
    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const res = await fetch(`${api}/api/organizations/${organizationSlug}/auctions/${auction.id}/${action}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body:
          action === "offers"
            ? JSON.stringify({ amount: counterAmount, notes: sellerNotes || null })
            : action === "relaunch"
              ? JSON.stringify({ type: relaunchType, durationMinutes: relaunchDurationMinutes })
              : undefined,
      });

      if (res.ok) {
        if (action === "relaunch") {
          const data = await res.json();
          if (data?.id) {
            navigate(`/${organizationSlug}/mine-auksjoner/${data.id}/rediger`);
            return;
          }
        }
        onRefresh?.();
      } else {
        const data = await res.json();
        alert(data.error || "Noe gikk galt");
      }
    } catch (err) {
      console.error(err);
      alert("Noe gikk galt");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBuyerAction = async (action: "accept" | "reject" | "offers", offerId?: string) => {
    if (
      action === "accept"
      && !window.confirm("Ved å godta motbudet inngår du en bindende avtale om kjøp av bilen etter Forhandlerbørsens vilkår. Vil du fortsette?")
    ) return;

    setSubmitting(true);
    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const url = offerId
        ? `${api}/api/auctions/${auction.id}/offers/${offerId}/${action}`
        : `${api}/api/auctions/${auction.id}/offers`;

      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: action === "offers"
          ? JSON.stringify({ amount: buyerCounterAmount, notes: buyerNotes || null, organizationSlug })
          : JSON.stringify({ organizationSlug }),
      });

      if (res.ok) {
        onRefresh?.();
      } else {
        const data = await res.json();
        alert(data.error || "Noe gikk galt");
      }
    } catch (err) {
      console.error(err);
      alert("Noe gikk galt");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (type: "contract" | "invoice", id: string) => {
    setDownloadingId(id);
    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const endpoint = type === "contract"
        ? `${api}/api/organizations/${organizationSlug}/documents/records/${id}/download-url?variant=signed`
        : `${api}/api/organizations/${organizationSlug}/accounting/records/${id}/download-url`;

      const res = await fetch(endpoint, { credentials: "include" });
      if (!res.ok) throw new Error("Kunne ikke hente nedlastingslenke");
      const { downloadUrl } = await res.json();
      window.open(downloadUrl, "_blank");
    } catch (err) {
      console.error(`Failed to download ${type}`, err);
      alert(`Kunne ikke laste ned ${type === "contract" ? "kontrakten" : "fakturaen"}.`);
    } finally {
      setDownloadingId(null);
    }
  };

  // --- Status Badge ---
  const statusBadge = (() => {
    if (isSeller) return <SellerStatusBadge status={auction.status} />;
    // Buyer purchase badge
    if (["pending_seller_decision", "negotiating"].includes(auction.status)) {
      return (
        <div className="flex items-center gap-1.5 rounded-full bg-[#44a044] backdrop-blur px-3 py-1.5 text-[11px] font-black uppercase tracking-wider text-white shadow-lg shadow-green-900/20 ring-2 ring-white/20 animate-in zoom-in-50 duration-300">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Avventer svar
        </div>
      );
    }
    if ((ACCEPTED_SALE_STATUSES as readonly string[]).includes(auction.status)) {
      return (
        <div className="flex items-center gap-1.5 rounded-full bg-[#44a044] backdrop-blur px-3 py-1.5 text-[11px] font-black uppercase tracking-wider text-white shadow-lg shadow-green-900/20 ring-2 ring-white/20 animate-in zoom-in-50 duration-300">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Godtatt
        </div>
      );
    }
    // Unknown or non-accepted status — fall back to the neutral seller badge
    // rather than incorrectly claiming the sale was accepted.
    return <SellerStatusBadge status={auction.status} />;
  })();

  const cardStyle = isSeller && ["pending_seller_decision", "negotiating", "no_bids"].includes(auction.status)
    ? { borderColor: "var(--accent)", boxShadow: "0 4px 20px rgba(var(--accent-rgb), 0.15)" }
    : undefined;

  return (
    <AuctionCardWrapper
      onClick={() => {
        if (isEditable) navigate(`/${organizationSlug}/mine-auksjoner/${auction.id}/rediger`);
        else navigate(`/${organizationSlug}/budrunder/${auction.id}`);
      }}
      style={cardStyle}
    >
      <AuctionCardImage
        image={auction.primaryImage}
        title={auction.title}
        topLeft={statusBadge}
        topRight={
          auction.type === "flash" ? <FlashBadge /> :
          isSeller && isEditable ? (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/${organizationSlug}/mine-auksjoner/${auction.id}/rediger`); }}
              className="flex h-9 items-center justify-center rounded-full bg-white/90 backdrop-blur px-4 text-xs font-bold text-primary shadow-sm transition-all hover:scale-110 active:scale-90"
            >
              {auction.status === "draft" ? "Fortsett" : "Rediger"}
            </button>
          ) : undefined
        }
        bottomRight={
          !isSeller && onToggleFavorite ? (
            <FavoriteButton isFavorite={isFavorite} onToggle={() => onToggleFavorite(auction.id)} />
          ) : undefined
        }
      />

      <div className="flex flex-1 flex-col justify-between p-6">
        <div>
          <AuctionCardHeader
            title={auction.title}
            details={details}
            bidCount={auction.bidCount}
            referenceNumber={auction.referenceNumber}
            registrationNumber={v?.registrationNumber}
            registrationBadgeClass={mode === "buyer" ? "bg-green-50 text-green-700 border-green-200" : undefined}
            timingSlot={
              auction.status === "live" && auction.endsAt
                ? <LiveTimingBadge endsAt={auction.endsAt} />
                : <EndedTimingBadge
                    closedAt={auction.closedAt}
                    endsAt={auction.endsAt}
                    createdAt={auction.createdAt}
                    label={mode === "buyer" && auction.closedAt && auction.status !== "pending_seller_decision" ? "Kjøpt" : undefined}
                  />
            }
          />

          {/* Seller Decision Panel */}
          {isSeller && ["pending_seller_decision", "negotiating"].includes(auction.status) && (
            <SellerDecisionPanel
              auction={auction}
              submitting={submitting}
              showCounter={showCounter}
              setShowCounter={setShowCounter}
              counterAmount={counterAmount}
              setCounterAmount={setCounterAmount}
              sellerNotes={sellerNotes}
              setSellerNotes={setSellerNotes}
              onAction={handleAction}
            />
          )}

          {/* Buyer Negotiation Panel */}
          {!isSeller && ["pending_seller_decision", "negotiating"].includes(auction.status) && (
            <BuyerNegotiationPanel
              auction={auction}
              submitting={submitting}
              showBuyerCounter={showBuyerCounter}
              setShowBuyerCounter={setShowBuyerCounter}
              buyerCounterAmount={buyerCounterAmount}
              setBuyerCounterAmount={setBuyerCounterAmount}
              buyerNotes={buyerNotes}
              setBuyerNotes={setBuyerNotes}
              onAction={handleBuyerAction}
            />
          )}

          {/* Relaunch Panel (Seller) — no_bids = never had bids, no_sale = seller rejected the highest bid */}
          {isSeller && (auction.status === "no_bids" || auction.status === "no_sale") && (
            <NoBidsPanel
              status={auction.status}
              submitting={submitting}
              showRelaunch={showRelaunch}
              setShowRelaunch={setShowRelaunch}
              relaunchType={relaunchType}
              setRelaunchType={setRelaunchType}
              relaunchDurationMinutes={relaunchDurationMinutes}
              setRelaunchDurationMinutes={setRelaunchDurationMinutes}
              onAction={handleAction}
            />
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between border-t border-border pt-4 gap-4">
            <div className="flex flex-col">
              <p className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">
                {priceLabel}
              </p>
              <p className="text-3xl font-black text-primary" style={{ fontFamily: "var(--font-display)" }}>
                {formatCurrency(displayPrice)}
              </p>
            </div>

            {!isEditable && auction.status !== "pending_seller_decision" && auction.status !== "no_bids" && auction.status !== "live" && (
              <div className="flex items-center gap-2 text-xs font-bold text-accent underline">Se detaljer</div>
            )}
          </div>

          {/* Post-sale Actions */}
          <div className="flex flex-col gap-3">
            <FooterStatusBanner status={auction.status} mode={mode} />

            <div className="flex flex-wrap gap-3">
              {mode === "buyer" && ["sold", "contract_pending", "invoice_pending", "payment_pending", "ready_for_pickup"].includes(auction.status) && !auction.transportOrdered && (
                <button
                  onClick={(e) => { e.stopPropagation(); }}
                  className="flex-1 min-w-[160px] flex items-center justify-center gap-2 rounded-xl border-2 border-accent/20 bg-accent/5 py-3 text-sm font-bold text-accent transition-all hover:bg-accent hover:text-white active:scale-95"
                >
                  <Truck className="h-4 w-4" /> Sjekk transportpris
                </button>
              )}
              {auction.transportOrdered && (
                <div className="flex-1 min-w-[160px] flex items-center justify-center gap-2 rounded-xl border border-green-200 bg-green-50 py-3 text-sm font-bold text-green-700">
                  <Truck className="h-4 w-4" /> Transport bestilt
                </div>
              )}
              {auction.signedContractId && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDownload("contract", auction.signedContractId!); }}
                  disabled={downloadingId === auction.signedContractId}
                  className="flex-1 min-w-[160px] flex items-center justify-center gap-2 rounded-xl border-2 border-primary/20 bg-primary/5 py-3 text-sm font-bold text-primary transition-all hover:bg-primary hover:text-white active:scale-95 disabled:opacity-50"
                >
                  {downloadingId === auction.signedContractId ? <div className="h-4 w-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /> : <FileText className="h-4 w-4" />}
                  Kontrakt
                </button>
              )}
              {auction.invoices && auction.invoices.length > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); if (auction.invoices.length === 1) handleDownload("invoice", auction.invoices[0].id); else onRefresh?.(); }}
                  disabled={downloadingId !== null}
                  className="flex-1 min-w-[160px] flex items-center justify-center gap-2 rounded-xl border-2 border-accent/20 bg-accent/5 py-3 text-sm font-bold text-accent transition-all hover:bg-accent hover:text-white active:scale-95 disabled:opacity-50"
                >
                  <Download className="h-4 w-4" /> {auction.invoices.length > 1 ? "Se fakturaer" : "Faktura"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </AuctionCardWrapper>
  );
}

// --- Internal Helper Components ---

function SellerStatusBadge({ status }: { status: string }) {
  const labels: Record<string, { label: string; color: string; bg: string }> = {
    draft: { label: "Utkast", color: "text-muted-foreground", bg: "bg-muted/90" },
    pending_review: { label: "Venter på godkjenning", color: "text-blue-600", bg: "bg-blue-50/90" },
    approved: { label: "Godkjent", color: "text-green-600", bg: "bg-green-50/90" },
    changes_requested: { label: "Trenger endringer", color: "text-red-600", bg: "bg-red-50/90" },
    live: { label: "Aktiv", color: "text-primary", bg: "bg-white/90" },
    pending_seller_decision: { label: "Beslutning kreves", color: "text-accent", bg: "bg-accent/10 border-accent/20 backdrop-blur" },
    negotiating: { label: "Forhandling", color: "text-accent", bg: "bg-accent/10 border-accent/20 backdrop-blur" },
    no_bids: { label: "Ingen bud", color: "text-accent", bg: "bg-accent/10 border-accent/20 backdrop-blur" },
    sold: { label: "Solgt", color: "text-green-600", bg: "bg-green-50/90" },
    no_sale: { label: "Ikke solgt", color: "text-muted-foreground", bg: "bg-muted/90" },
  };

  const s = labels[status] || { label: status, color: "text-muted-foreground", bg: "bg-muted/90" };
  return (
    <div className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider shadow-sm backdrop-blur ${s.bg} ${s.color}`}>
      {s.label}
    </div>
  );
}

function SellerDecisionPanel({
  auction, submitting, showCounter, setShowCounter, counterAmount, setCounterAmount, sellerNotes, setSellerNotes, onAction,
}: {
  auction: any; submitting: boolean;
  showCounter: boolean; setShowCounter: (v: boolean) => void;
  counterAmount: number; setCounterAmount: (v: number) => void;
  sellerNotes: string; setSellerNotes: (v: string) => void;
  onAction: (action: "accept" | "reject" | "offers" | "relaunch") => void;
}) {
  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-accent/20 bg-white shadow-xl shadow-accent/5 transition-all">
      <div className="flex items-center justify-between bg-[#0a1e1e] px-6 py-4">
        <p className="text-sm font-black uppercase tracking-widest text-white" style={{ fontFamily: "var(--font-display)" }}>
          {auction.status === "negotiating" ? "Forhandling" : "Beslutning kreves"}
        </p>
        <div className="flex flex-col items-end">
          <p className="text-[10px] font-bold text-white/40 uppercase tracking-tighter">Utbetaling</p>
          <span className="text-xl font-black text-white" style={{ fontFamily: "var(--font-display)" }}>
            {formatCurrency(auction.sellerPayout)}
          </span>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 gap-3">
          <button
            onClick={(e) => { e.stopPropagation(); void onAction("accept"); }}
            disabled={submitting}
            className="group relative flex h-14 items-center justify-center gap-2 rounded-2xl bg-[#44a044] text-sm font-black text-white shadow-lg shadow-green-600/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
          >
            <Check className="h-4 w-4" />
            Godta bud
          </button>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={(e) => { e.stopPropagation(); setShowCounter(!showCounter); }}
              disabled={submitting}
              className={`flex h-12 items-center justify-center rounded-2xl border-2 transition-all active:scale-95 disabled:opacity-50 text-xs font-black ${showCounter ? "border-accent bg-accent text-white" : "border-border bg-white text-primary hover:bg-muted"}`}
            >
              <MessageSquare className="mr-2 h-3.5 w-3.5" />
              {showCounter ? "Avbryt" : "Gi motbud"}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); void onAction("reject"); }}
              disabled={submitting}
              className="flex h-12 items-center justify-center rounded-2xl border-2 border-red-100 bg-red-50/30 text-xs font-black text-red-600 transition-all hover:bg-red-50 active:scale-95 disabled:opacity-50"
            >
              <X className="mr-2 h-3.5 w-3.5" />
              Avslå
            </button>
          </div>
        </div>

        <p className="text-[11px] leading-relaxed text-muted-foreground text-center px-4">
          Ved å godta budet aksepterer du en bindende handel etter Forhandlerbørsens vilkår.
        </p>

        {showCounter && (
          <div className="space-y-5 border-t border-border pt-6 animate-in fade-in slide-in-from-top-4 duration-300" onClick={(e) => e.stopPropagation()}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 px-1">Motbud (kr)</label>
                <input
                  type="number" value={counterAmount}
                  onChange={(e) => setCounterAmount(Number(e.target.value))}
                  className="h-12 w-full rounded-2xl border-2 border-border bg-white px-4 text-sm font-bold shadow-sm outline-none transition-all focus:border-accent/30 focus:ring-4 focus:ring-accent/5"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 px-1">Notat (valgfritt)</label>
                <input
                  type="text" value={sellerNotes}
                  onChange={(e) => setSellerNotes(e.target.value)}
                  placeholder="F.eks. inkl. vinterhjul"
                  className="h-12 w-full rounded-2xl border-2 border-border bg-white px-4 text-sm font-bold shadow-sm outline-none transition-all focus:border-accent/30 focus:ring-4 focus:ring-accent/5"
                />
              </div>
            </div>
            <button
              onClick={() => onAction("offers")}
              disabled={submitting || counterAmount <= auction.currentBid}
              className="w-full h-12 rounded-2xl bg-accent text-sm font-black text-white shadow-lg shadow-accent/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
            >
              Send motbud
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function BuyerNegotiationPanel({
  auction, submitting, showBuyerCounter, setShowBuyerCounter, buyerCounterAmount, setBuyerCounterAmount, buyerNotes, setBuyerNotes, onAction,
}: {
  auction: any; submitting: boolean;
  showBuyerCounter: boolean; setShowBuyerCounter: (v: boolean) => void;
  buyerCounterAmount: number; setBuyerCounterAmount: (v: number) => void;
  buyerNotes: string; setBuyerNotes: (v: string) => void;
  onAction: (action: "accept" | "reject" | "offers", offerId?: string) => void;
}) {
  const latestOffer = auction.offers?.[auction.offers.length - 1];
  const isSellerOffer = latestOffer?.offeredBy === "seller" && latestOffer?.status === "pending";
  const isBuyerOffer = latestOffer?.offeredBy === "buyer";

  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-primary/20 bg-white shadow-xl shadow-primary/5 transition-all">
      <div className="flex items-center justify-between bg-[#0a1e1e] px-6 py-4">
        <p className="text-sm font-black uppercase tracking-widest text-white" style={{ fontFamily: "var(--font-display)" }}>
          {auction.status === "negotiating" ? "Forhandling" : "Beslutning kreves"}
        </p>
        {auction.offers && auction.offers.length > 0 && (
          <span className="rounded bg-white/10 px-2 py-1 text-[10px] font-bold text-white/80 border border-white/20">
            Runde {Math.ceil(auction.offers.length / 2)}
          </span>
        )}
      </div>

      <div className="p-6 space-y-6">
        {isSellerOffer ? (
          <div className="space-y-6">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-[11px] font-black uppercase tracking-widest text-muted-foreground/60">Selgers motbud</p>
                <p className="text-4xl font-black text-primary" style={{ fontFamily: "var(--font-display)" }}>
                  {formatCurrency(latestOffer.amount)}
                </p>
              </div>
              <TrendingUp className="h-5 w-5 text-accent opacity-50" />
            </div>

            {latestOffer.notes && (
              <div className="rounded-2xl border-2 border-dashed border-border bg-muted/20 p-5 text-center">
                <p className="text-sm font-medium italic text-muted-foreground">
                  &ldquo;{latestOffer.notes}&rdquo;
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3">
              <button
                onClick={(e) => { e.stopPropagation(); void onAction("accept", latestOffer.id); }}
                disabled={submitting}
                className="group relative flex h-14 items-center justify-center gap-2 rounded-2xl bg-[#44a044] text-sm font-black text-white shadow-lg shadow-green-600/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
              >
                <Check className="h-4 w-4" />
                Godta motbud
              </button>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowBuyerCounter(!showBuyerCounter); }}
                  disabled={submitting}
                  className={`flex h-12 items-center justify-center rounded-2xl border-2 border-border bg-white text-xs font-black text-primary transition-all hover:bg-muted active:scale-95 disabled:opacity-50 ${showBuyerCounter ? "bg-muted" : ""}`}
                >
                  {showBuyerCounter ? "Avbryt" : "Gi nytt motbud"}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); void onAction("reject", latestOffer.id); }}
                  disabled={submitting}
                  className="flex h-12 items-center justify-center rounded-2xl border-2 border-red-100 bg-red-50/30 text-xs font-black text-red-600 transition-all hover:bg-red-50 active:scale-95 disabled:opacity-50"
                >
                  Avslå
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="rounded-2xl border border-dashed border-border bg-muted/20 p-6">
              <p className="text-sm font-black uppercase tracking-widest text-primary mb-3">Forhandling pågår</p>
              <p className="text-xs font-medium leading-relaxed text-muted-foreground/80 italic">
                {isBuyerOffer
                  ? "Ditt motbud er sendt. Vi venter på svar fra selger."
                  : "Auksjonen ble ikke solgt automatisk. Du kan gi et motbud til selger for å starte en forhandling."}
              </p>
            </div>

            {!showBuyerCounter && (!isBuyerOffer) && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowBuyerCounter(true); }}
                className="w-full h-12 rounded-2xl bg-primary text-sm font-black text-white shadow-lg shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
              >
                Gi motbud
              </button>
            )}
          </div>
        )}

        {showBuyerCounter && (
          <div className="space-y-5 border-t border-border pt-6 animate-in fade-in slide-in-from-top-4 duration-300" onClick={(e) => e.stopPropagation()}>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 px-1">Ditt motbud (kr)</label>
                <input
                  type="number" value={buyerCounterAmount}
                  onChange={(e) => setBuyerCounterAmount(Number(e.target.value))}
                  className="h-12 w-full rounded-2xl border-2 border-border bg-white px-4 text-sm font-bold shadow-sm outline-none transition-all focus:border-primary/30 focus:ring-4 focus:ring-primary/5"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 px-1">Notat til selger (valgfritt)</label>
                <textarea
                  value={buyerNotes}
                  onChange={(e) => setBuyerNotes(e.target.value)}
                  placeholder="F.eks. inkludert omregistrering..."
                  className="w-full rounded-2xl border-2 border-border bg-white px-4 py-3 text-sm font-medium shadow-sm outline-none transition-all focus:border-primary/30 focus:ring-4 focus:ring-primary/5 min-h-[100px] resize-none"
                />
              </div>
            </div>
            <button
              onClick={() => onAction("offers")}
              disabled={submitting || buyerCounterAmount <= auction.currentBid}
              className="w-full h-12 rounded-2xl bg-primary text-sm font-black text-white shadow-lg shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
            >
              Send motbud
            </button>
          </div>
        )}
      </div>

      {isBuyerOffer && (
        <div className="bg-muted/30 px-6 py-4 border-t border-border flex items-center justify-center gap-3">
          <div className="flex h-6 w-6 items-center justify-center rounded-full border border-primary/20 bg-white">
            <Check className="h-3 w-3 text-primary" />
          </div>
          <p className="text-[10px] font-black uppercase tracking-widest text-primary">
            Avventer svar fra selger &ndash; vi holder fingre og t&aelig;r for deg!
          </p>
        </div>
      )}
    </div>
  );
}

function NoBidsPanel({
  status, submitting, showRelaunch, setShowRelaunch, relaunchType, setRelaunchType, relaunchDurationMinutes, setRelaunchDurationMinutes, onAction,
}: {
  status: "no_bids" | "no_sale";
  submitting: boolean;
  showRelaunch: boolean; setShowRelaunch: (v: boolean) => void;
  relaunchType: "regular" | "flash"; setRelaunchType: (v: "regular" | "flash") => void;
  relaunchDurationMinutes: number; setRelaunchDurationMinutes: (v: number) => void;
  onAction: (action: "accept" | "reject" | "offers" | "relaunch") => void;
}) {
  const isNoSale = status === "no_sale";
  const headerLabel = isNoSale ? "Bilen ble ikke solgt" : "Ingen bud mottatt";
  const helperText = isNoSale
    ? "Du kan opprette en ny utgave av auksjonen. Det forrige budet blir ikke videreført."
    : "Velg om bilen skal markeres som ikke solgt, eller opprett en ny utgave av auksjonen med ny varighet og auksjonstype.";
  return (
    <div className="mt-6 space-y-4 rounded-xl bg-accent/5 p-4 border border-accent/10">
      <p className="text-[11px] font-black uppercase tracking-widest text-[#1a2e35]">{headerLabel}</p>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); setShowRelaunch(!showRelaunch); }}
          disabled={submitting}
          className="flex h-10 items-center justify-center gap-2 rounded-xl bg-accent px-4 text-xs font-black text-white shadow-sm hover:scale-105 active:scale-95 disabled:opacity-50 transition-all"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {showRelaunch ? "Lukk relansering" : "Relanser"}
        </button>
        {!isNoSale && (
          <button
            onClick={(e) => { e.stopPropagation(); onAction("reject"); }}
            disabled={submitting}
            className="flex h-10 items-center justify-center gap-2 rounded-xl border border-border bg-white px-4 text-xs font-black text-primary hover:bg-muted active:scale-95 disabled:opacity-50 transition-all"
          >
            <X className="h-3.5 w-3.5" />
            Ikke solgt
          </button>
        )}
      </div>
      <p className="text-[11px] leading-relaxed text-muted-foreground">
        {helperText}
      </p>

      {showRelaunch && (
        <div className="space-y-4 border-t border-accent/10 pt-4 animate-in fade-in slide-in-from-top-2 duration-300" onClick={(e) => e.stopPropagation()}>
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Auksjonstype</label>
            <div className="flex flex-wrap gap-2">
              {([
                { label: "Vanlig auksjon", value: "regular" as const },
                { label: "Lynauksjon", value: "flash" as const },
              ]).map((option) => {
                const active = relaunchType === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setRelaunchType(option.value);
                      setRelaunchDurationMinutes(option.value === "flash" ? 60 : 1440);
                    }}
                    className="rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors"
                    style={{
                      border: `1.5px solid ${active ? "var(--accent)" : "var(--border)"}`,
                      background: active ? "var(--accent)" : "white",
                      color: active ? "white" : "var(--foreground)",
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Varighet</label>
            <div className="flex flex-wrap gap-2">
              {(relaunchType === "flash"
                ? [
                  { label: "15 min", value: 15 },
                  { label: "1 time", value: 60 },
                  { label: "2 timer", value: 120 },
                ]
                : [
                  { label: "24 timer", value: 1440 },
                  { label: "48 timer", value: 2880 },
                  { label: "72 timer", value: 4320 },
                ]).map((option) => {
                  const active = relaunchDurationMinutes === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setRelaunchDurationMinutes(option.value)}
                      className="rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors"
                      style={{
                        border: `1.5px solid ${active ? "var(--accent)" : "var(--border)"}`,
                        background: active ? "var(--accent)" : "white",
                        color: active ? "white" : "var(--foreground)",
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
            </div>
          </div>

          <button
            onClick={() => onAction("relaunch")}
            disabled={submitting}
            className="w-full rounded-xl bg-accent py-2.5 text-xs font-black text-white hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 shadow-md shadow-accent/20"
          >
            Opprett relansering
          </button>
        </div>
      )}
    </div>
  );
}
